import { useState } from 'react'
import { Button, Input, Modal } from '@/components/ui'

export interface DriveFolderDialogProps {
  title: string
  submitLabel: string
  initialName?: string
  isPending: boolean
  onClose: () => void
  onSubmit: (name: string) => void
}

/** Name a new folder or rename one. Mount it only while it is open. */
export function DriveFolderDialog({
  title,
  submitLabel,
  initialName = '',
  isPending,
  onClose,
  onSubmit,
}: DriveFolderDialogProps): React.ReactElement {
  const [name, setName] = useState(initialName)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Enter a folder name')
      return
    }
    if (/[/\\]/.test(trimmed)) {
      setError('Folder names cannot contain slashes')
      return
    }
    setError(null)
    onSubmit(trimmed)
  }

  return (
    <Modal open onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="driveFolderName"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Folder name
          </label>
          <Input
            id="driveFolderName"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={80}
            autoFocus
          />
          {error ? <p className="mt-1 text-sm text-danger">{error}</p> : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" loading={isPending} className="whitespace-nowrap">
            {submitLabel}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

DriveFolderDialog.displayName = 'DriveFolderDialog'
